// MCHD Room Scheduler - Dashboard Layout Component
// Main layout with navbar and content area
// Modified: 08-10-2025 15:35:00 - Added collapsible sidebar support (Phase 2)

import { ReactNode } from 'react';
import { Box, AppShell, Group, Text } from '@mantine/core';
import { User } from '../api';
import { Navbar } from '../components/organisms/Navbar';
import { UserAvatarDropdown } from '../components/molecules/UserAvatarDropdown';
import { usePlannerStore } from '../stores/plannerStore';

/**
 * Props for the DashboardLayout component
 */
interface DashboardLayoutProps {
  user: User;
  onLogout: () => void;
  children: ReactNode;
}

/**
 * Main dashboard layout with navbar and content area
 * 
 * Enhanced with collapsible sidebar support (Phase 2 - 2025 UX)
 * 
 * @param user - Current user data
 * @param onLogout - Logout handler function
 * @param children - Page content to render
 * @returns JSX element
 */
export function DashboardLayout({ user, onLogout, children }: DashboardLayoutProps): JSX.Element {
  // Get sidebar collapsed state from planner store
  const sidebarCollapsed = usePlannerStore((state) => state.ui.sidebarCollapsed);

  return (
    <AppShell
      navbar={{
        width: sidebarCollapsed ? 80 : 300,
        breakpoint: 'sm',
        collapsed: { mobile: true },
      }}
      header={{
        height: 60,
      }}
      padding="md"
    >
      <AppShell.Navbar>
        <Navbar
          userRole={user.role}
          onLogout={onLogout}
          collapsed={sidebarCollapsed}
        />
      </AppShell.Navbar>

      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" align="center">
          <Text size="lg" fw={600} c="dark">
            MCHD Room Scheduler
          </Text>
          <UserAvatarDropdown
            user={{
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
            }}
            onLogout={onLogout}
          />
        </Group>
      </AppShell.Header>
      
      <AppShell.Main>
        <Box
          style={{
            marginLeft: 0, // AppShell handles the margin
            minHeight: '100vh',
            backgroundColor: 'var(--mantine-color-gray-0)',
          }}
        >
          {children}
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
